import * as React from 'react';
import * as yup from 'yup';
import {faExclamationCircle, faCheckCircle} from '@fortawesome/pro-solid-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {FormProvider, useForm} from 'react-hook-form';
import {useState} from 'react';
import {yupResolver} from '@hookform/resolvers/yup';

import {gaLegacyCustomEvent} from '../../../client/ga/ga-legacy.functions';
import {InputGroup} from '../../ui/forms/InputGroup/InputGroup';
import {ItemsService} from '../../../client/items/items.service';
import {Item} from '../item.class';
import {User} from '../../users/user.class';
import {useService} from '../../react/ServiceContext';
import {validateEmail} from '../../tools/yup-validators.functions';
import Button from '../../ui/Buttons/Button';

export interface emailForm {
    restockEmail: string;
}

interface RestockNotificationFormProps {
    item: Item;
    user: User;
}

export const RestockNotificationForm = ({item, user}: RestockNotificationFormProps) => {
    const [hadError, setHadError] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const itemService: ItemsService = useService(`itemsService`);

    const useFormReturn = useForm({
        defaultValues: {restockEmail: user.loginName || ``},
        mode: `onSubmit`,
        resolver: yupResolver(yup.object({restockEmail: validateEmail()})),
    });
    const inputEmail = useFormReturn.watch(`restockEmail`);

    /**
     * TBD
     * @param formValues
     */
    const formSubmit = (formValues: emailForm) => {
        itemService
            .restockEmail({account: user.activeAccount, email: formValues.restockEmail, item: item.id, login: user.loginName})
            .then((restockEmailRes) => {
                if (restockEmailRes.result === `OK`) {
                    setIsSuccess(true);
                    gaLegacyCustomEvent({eventAction: `back_in_stock_form`, eventCategory: `IDP`, eventLabel: `success`});
                }
            })
            .catch(() => {
                setHadError(true);
                gaLegacyCustomEvent({eventAction: `back_in_stock_form`, eventCategory: `IDP`, eventLabel: `fail`});
            });
    };

    /**
     * Template
     */
    if (
        !(
            user.isLoggedIn() &&
            user.isAccountLevel() &&
            !item.ondemandDays_i &&
            !item.pandemicItemBlocked &&
            !item.inStock &&
            !item.isdiscontinue &&
            item.itemtype &&
            item.itemtype.toUpperCase() === `I`
        )
    ) {
        return <></>;
    }
    return (
        <FormProvider {...useFormReturn}>
            <form
                method="post"
                name="restockNotificationForm"
                onSubmit={(event) => {
                    useFormReturn
                        .handleSubmit(formSubmit)(event)
                        .catch(() => {
                            setHadError(true);
                        });
                }}
            >
                <div className="tw-border tw-bg-gray-100 tw-p-3 tw-mb-4">
                    {isSuccess && (
                        <>
                            <div className="tw-mb-2">
                                <FontAwesomeIcon
                                    className="tw-mr-2"
                                    color="#008000"
                                    icon={faCheckCircle}
                                />
                                Success
                            </div>
                            <div>We will send an email to {inputEmail} when the item is back in stock.</div>
                        </>
                    )}
                    {hadError && (
                        <>
                            <div className="tw-mb-2">
                                <FontAwesomeIcon
                                    className="tw-mr-2"
                                    color="#c8102e"
                                    icon={faExclamationCircle}
                                />
                                Error
                            </div>
                            <div>
                                We were unable to complete your request. Please{' '}
                                <Button
                                    onClick={() => {
                                        location.reload();
                                    }}
                                    size="sm"
                                    variant="link"
                                >
                                    Try Again
                                </Button>
                                .
                            </div>
                        </>
                    )}
                    {!hadError && !isSuccess && (
                        <div>
                            <div className="tw-mb-3">Email me when this item is back in stock</div>
                            <InputGroup
                                autocorrect="off"
                                error={useFormReturn?.formState?.errors?.restockEmail?.message}
                                label="Email Address"
                                name="restockEmail"
                                required
                                {...useFormReturn.register(`restockEmail`)}
                                button={
                                    <Button
                                        className="!tw-w-[120px] !tw-text-base"
                                        size="lg"
                                        type="submit"
                                        variant="outline-secondary"
                                    >
                                        Notify Me
                                    </Button>
                                }
                            />
                        </div>
                    )}
                </div>
            </form>
        </FormProvider>
    );
};
